// lib/image-decode-client.ts
import { decodeWorkerPixels, sendWorkerRequest, type WirePixels } from '#lib/worker-rpc';

interface ImageDecodeWorkerRequest {
  messageId: string;
  url: string;
}

type ImageDecodeWorkerResponse =
  | { messageId: string; error: string }
  | { messageId: string; result: WirePixels };

// Layers load twelve at a time and a decoded 16K bitmap weighs half a gigabyte: decode one image at a time.
let decodeQueue: Promise<unknown> = Promise.resolve();

const readBlobPixels = async (blob: Blob): Promise<WirePixels> => {
  const bitmap = await createImageBitmap(blob);
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  try {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Failed to acquire 2D context for image extraction.');
    ctx.drawImage(bitmap, 0, 0);
    // Before the readback, so a 16K decode never holds the bitmap and the pixels at once.
    bitmap.close();
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return { buffer: data.buffer as ArrayBuffer, width, height };
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
};

export const handleImageDecodeWorkerRequest = async (
  { messageId, url }: ImageDecodeWorkerRequest,
): Promise<{ response: ImageDecodeWorkerResponse; transfer: Transferable[] }> => {
  try {
    const response = await fetch(url);
    // An <img> still draws an image served with an error status: the main-thread fallback keeps that call.
    if (!response.ok) throw new Error(`Image request answered ${response.status}.`);
    const blob = await response.blob();
    const pixels = decodeQueue.then(() => readBlobPixels(blob));
    decodeQueue = pixels.catch(() => undefined);
    const result = await pixels;
    return { response: { messageId, result }, transfer: [result.buffer] };
  } catch (error) {
    return {
      response: { messageId, error: error instanceof Error ? error.message : 'Unknown image decode worker error.' },
      transfer: [],
    };
  }
};

// No silence timeout: a healthy worker says nothing for the whole download of an XXL layer (4.9 s measured on a
// fast link), and a worker that fails to load reports it through its error event.
export const decodeImageUrlInWorker = (worker: Worker, url: string): Promise<ImageData> =>
  sendWorkerRequest<Omit<ImageDecodeWorkerRequest, 'messageId'>, WirePixels>(
    worker,
    // Inside the worker a relative URL would resolve against the worker script.
    { url: new URL(url, document.baseURI).href },
    [],
    { label: 'Image decode', errorEventMarksUnavailable: true },
  ).then((pixels) => decodeWorkerPixels('Image decode', pixels));
